import React, { FC } from "react";
import {
  Form,
  InputNumber,
  Button,
  DatePicker,
  Upload,
  Input,
  Select,
  Spin,
} from "antd";
import { UploadOutlined, LoadingOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { updateFormData } from "../../../../redux/actions/investmentForm";
import { useInvestmentAmountDetails } from "../../../../custom-hooks/useInvestmentAmountDetails";
import { selectAvailableWallets } from "../../../../redux/selectors/investmentFormSelectors";
import styles from "../Components.module.scss";

const { Option } = Select;

// Simplified props
interface InvestmentModalAmountDetailsProps {
  form: any;
  data?: any; // Investment product ID
}

const InvestmentModalAmountDetails: FC<InvestmentModalAmountDetailsProps> = ({
  form,
  data,
}) => {
  const dispatch = useDispatch();

  // Get available wallets from Redux store
  const availableWallets = useSelector(selectAvailableWallets);

  // Use our custom hook
  const {
    currencyCode,
    fileList,
    handlePrincipalAmountChange,
    handleTopUpAmountChange,
    handleEffectiveDateChange,
    handleFileUpload,
    handleFileRemove,
  } = useInvestmentAmountDetails(form, data?.id);

  // Watch form values
  const principalAmount = Form.useWatch("principal_amount", form);
  const topUpAmount = Form.useWatch("amount_to_credit", form);
  const selectedWalletId = Form.useWatch("wallet_id", form);

  // Get selected wallet and balance
  const selectedWallet = availableWallets.find(
    (wallet: { id: any }) => wallet.id === selectedWalletId,
  );
  const walletBalance = selectedWallet?.availableBalance || 0;
  const loadingWallet = false; // We already have the wallet data

  // Calculate required top-up based on investment amount and wallet balance
  const getRequiredTopUp = (amount: number) => {
    if (!amount) return 0;
    return Math.max(0, amount - walletBalance);
  };

  return (
    <div className={`investment-modal-amount-details ${styles.formContainer}`}>
      <div className={styles.modalTitle}>Investment Details</div>

      <div className={styles.formWrapper}>
        {/* Hidden fields to store wallet balance in multiple ways */}
        <Form.Item name="wallet_balance" hidden={true}>
          <Input type="hidden" />
        </Form.Item>

        <Form.Item name="wallet_balance_backup" hidden={true}>
          <Input type="hidden" />
        </Form.Item>

        <Form.Item
          name="wallet_id"
          label={<span className={styles.formLabel}>Select Wallet</span>}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          rules={[{ required: true, message: "Please select wallet" }]}
        >
          {loadingWallet ? (
            <div
              className={`${styles.inputStyle} ${styles.disabledInput}`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              />
              <span style={{ marginLeft: "12px" }}>
                Loading wallet information...
              </span>
            </div>
          ) : (
            <Select
              placeholder="select wallet"
              className={styles.selectStyle}
              size="large"
              onChange={(value) => {
                // Find the selected wallet to get balance
                const selectedWallet = availableWallets.find(
                  (wallet: { id: any }) => wallet.id === value,
                );

                if (selectedWallet) {
                  dispatch(
                    updateFormData({
                      wallet_id: value,
                      wallet_balance: selectedWallet.availableBalance,
                      wallet_balance_backup: selectedWallet.availableBalance,
                      currency: selectedWallet.currency, // Update currency to match the selected wallet
                    }),
                  );

                  // Validate the principal_amount field if it exists
                  // This will update any error messages related to wallet balance
                  if (form.getFieldValue("principal_amount")) {
                    form.validateFields([
                      "principal_amount",
                      "amount_to_credit",
                    ]);
                  }
                } else {
                  dispatch(
                    updateFormData({
                      wallet_id: value,
                    }),
                  );
                }
              }}
              notFoundContent={
                <div style={{ padding: "10px", textAlign: "center" }}>
                  {availableWallets.length === 0
                    ? "No wallets available for this investor. They may need to create a wallet first."
                    : "No matching wallets found"}
                </div>
              }
            >
              {availableWallets && availableWallets.length > 0 ? (
                availableWallets.map(
                  (wallet: {
                    id: React.Key | null | undefined;
                    walletProviderType: any;
                    currency: any;
                    accountNumber: any;
                  }) => (
                    <Option key={wallet.id} value={wallet.id}>
                      {`${wallet.walletProviderType || "Wallet"}-${wallet.currency || "N/A"} (${wallet.accountNumber || "No account number"})`}
                    </Option>
                  ),
                )
              ) : (
                <Option disabled value="no-wallets">
                  No wallets available
                </Option>
              )}
            </Select>
          )}
        </Form.Item>

        <Form.Item
          label={
            <span className={styles.formLabel}>
              Wallet Balance ({selectedWallet?.currency || currencyCode})
            </span>
          }
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          {!selectedWallet ? (
            <Input
              value="Select a wallet to see balance"
              disabled
              className={`${styles.inputStyle} ${styles.disabledInput}`}
              size="large"
            />
          ) : (
            <Input
              value={walletBalance.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              disabled
              className={`${styles.inputStyle} ${styles.disabledInput}`}
              size="large"
            />
          )}
        </Form.Item>

        <Form.Item
          name="amount_to_credit"
          label={
            <div>
              <span className={styles.formLabel}>
                Wallet Top-up Amount ({selectedWallet?.currency || currencyCode}
                )
              </span>
              {principalAmount &&
                getRequiredTopUp(Number(principalAmount)) > 0 && (
                  <span
                    style={{
                      marginLeft: "12px",
                      fontSize: "14px",
                      color:
                        getRequiredTopUp(Number(principalAmount)) >
                        (topUpAmount || 0)
                          ? "#ff4d4f"
                          : "#52c41a",
                      fontWeight: 500,
                    }}
                  >
                    (Required:{" "}
                    {getRequiredTopUp(Number(principalAmount)).toLocaleString()}{" "}
                    {selectedWallet?.currency || currencyCode})
                  </span>
                )}
            </div>
          }
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          tooltip={
            !selectedWallet
              ? "Please select a wallet first"
              : principalAmount && getRequiredTopUp(Number(principalAmount)) > 0
                ? "If your wallet balance is insufficient for the investment amount, you must top-up the difference"
                : "Optional if your wallet balance is sufficient for the investment"
          }
          rules={[
            {
              validator: (_, value) => {
                if (!selectedWallet) {
                  return Promise.resolve(); // Don't validate if no wallet is selected
                }
                const required = getRequiredTopUp(Number(principalAmount));
                if (required > 0 && (!value || Number(value) < required)) {
                  return Promise.reject(
                    `Please top-up at least ${required.toLocaleString()} ${selectedWallet.currency}`,
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber
            className={styles.inputStyle}
            style={{
              width: "100%",
              borderColor:
                principalAmount &&
                getRequiredTopUp(Number(principalAmount)) > (topUpAmount || 0)
                  ? "#ff4d4f"
                  : undefined,
            }}
            formatter={(value) => {
              if (!value && value !== 0) return "";
              const parts = `${value}`.split(".");
              parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
              return parts.join(".");
            }}
            parser={(value: string | undefined) =>
              value ? Number(value.replace(/\$\s?|(,*)/g, "")) : 0
            }
            placeholder="0"
            size="large"
            precision={2}
            onChange={handleTopUpAmountChange}
          />
        </Form.Item>

        <Form.Item
          name="principal_amount"
          label={
            <div>
              <span className={styles.formLabel}>
                Investment Amount ({selectedWallet?.currency || currencyCode})
              </span>
              {principalAmount &&
                selectedWallet &&
                principalAmount > walletBalance && (
                  <span
                    style={{
                      marginLeft: "12px",
                      fontSize: "14px",
                      color: "#faad14",
                      fontWeight: 500,
                    }}
                  >
                    (Exceeds wallet balance by{" "}
                    {(Number(principalAmount) - walletBalance).toLocaleString()}{" "}
                    {selectedWallet?.currency || currencyCode})
                  </span>
                )}
            </div>
          }
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          tooltip={
            selectedWallet
              ? `Your current wallet balance is ${walletBalance.toLocaleString()} ${selectedWallet.currency}. If investment amount exceeds this, you must top-up the difference.`
              : "Please select a wallet first to see available balance"
          }
          rules={[
            { required: true, message: "Please enter investment amount" },
            {
              validator: (_, value) => {
                const minimumInvestment =
                  data?.investment_product_details?.[0]?.minimum_investment ||
                  0;
                if (value && value < minimumInvestment) {
                  return Promise.reject(
                    `Investment amount must be at least ${minimumInvestment}`,
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber
            className={styles.inputStyle}
            style={{ width: "100%" }}
            formatter={(value) => {
              if (!value && value !== 0) return "";
              const parts = `${value}`.split(".");
              parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
              return parts.join(".");
            }}
            parser={(value: string | undefined) =>
              value ? Number(value.replace(/\$\s?|(,*)/g, "")) : 0
            }
            placeholder="0"
            size="large"
            precision={2}
            onChange={handlePrincipalAmountChange}
          />
        </Form.Item>

        <Form.Item
          name="effective_date"
          label={<span className={styles.formLabel}>Effective Date</span>}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          tooltip="Select today or a past date. Future dates are not allowed."
          rules={[{ required: true, message: "Please select effective date" }]}
        >
          <DatePicker
            className={styles.inputStyle}
            style={{ width: "100%" }}
            onChange={handleEffectiveDateChange}
            disabledDate={(current) => {
              // Disable dates after today
              return current && current > moment().endOf("day");
            }}
            placeholder="Select date (today or past)"
          />
        </Form.Item>

        <Form.Item
          name="transaction_receipt"
          label={
            <span className={styles.formLabel}>Upload Transaction Receipt</span>
          }
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          rules={[
            {
              required: true,
              message: "Please upload the transaction receipt",
            },
          ]}
          validateTrigger={["onChange", "onBlur"]}
        >
          <Upload
            beforeUpload={handleFileUpload}
            maxCount={1}
            accept="image/png,image/jpeg,image/jpg,application/pdf"
            listType="text"
            fileList={fileList}
            onRemove={handleFileRemove}
            style={{ width: "100%" }}
          >
            <Button
              type="text"
              icon={<UploadOutlined />}
              className={styles.uploadButton}
            >
              Upload File
            </Button>
          </Upload>
        </Form.Item>
      </div>
    </div>
  );
};

export default InvestmentModalAmountDetails;
